import React, { useEffect, useState } from "react";
import {
  Container,
  Button,
  Group,
  Select,
  Text,
  ScrollArea,
  SimpleGrid,
  Loader,
  Divider,
  ActionIcon,
  Tooltip,
  Modal,
  TextInput,
  NumberInput,
  Textarea,
  Menu,
  Box,
} from "@mantine/core";
import { FcExpand } from "react-icons/fc";
import { DateInput } from "@mantine/dates";
import {
  getDocs,
  query,
  collection,
  where,
  orderBy,
  startAfter,
  getCountFromServer,
  limit,
  collectionGroup,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { IconArrowLeft, IconArrowRight, IconEdit, IconCash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import useBranchId from "../../hooks/useBranchId";
import { RootState } from "../../store/store";
import { logError } from "../ErrorLogService/ErrorLoggingService";
import { BenchTable } from "../pages/UI";
import PermissionButton from "../PermissionComponent/PermissionButton";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";


export default function RecurringExpenseTable() {
  const companyId = useAppSelector(
    (state: RootState) => state.session.company_id
  );
  const {
    branchesList,
    loading: branchLoading,
  } = useAppSelector((state) => state.branchList);
  const { branchId, setBranchId } = useBranchId(branchesList);

  const [recurringExpenses, setRecurringExpenses] = useState<any[]>([]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<string>("10");
  const [pageCursors, setPageCursors] = useState<any[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalResultCount, setTotalResultCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Record Payment States
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<any>(null);
  const [paymentAccountList, setPaymentAccountList] = useState<any[]>([]);
  const [paidThroughID, setPaidThroughID] = useState<string>("");
  const [paymentAmount, setPaymentAmount] = useState<number | "">("");
  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (companyId && branchId) {
      fetchAllRecurringExpenses("first");
    }
  }, [companyId, branchId]);

  useEffect(() => {
    if (companyId) {
      fetchBankingData();
    }
  }, [companyId]);

  const fetchBankingData = async () => {
    if (!companyId) return;
    try {
      const q = query(collectionGroup(db, "banking"), where("company_id", "==", companyId));
      const querySnapshot = await getDocs(q);
      const bankingData = querySnapshot.empty ? [] : querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      let allAccounts: any[] = [...bankingData];

      const cashDocRef = query(collectionGroup(db, "paymentcollection"), where("type", "==", "cash"), where("company_id", "==", companyId), limit(1));
      const cashDocSnap = await getDocs(cashDocRef);
      if (!cashDocSnap.empty && cashDocSnap.docs[0].data()?.status === "Active") {
         allAccounts.push({ value: "petty_cash", label: "Petty Cash", accountName: "Petty Cash" });
      }
      setPaymentAccountList(allAccounts);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRecordPayment = async () => {
    if (!paidThroughID || !selectedExpense || !companyId) return;
    setIsSubmitting(true);
    try {
      const selectedAccount = paymentAccountList.find((a) => a.id === paidThroughID || a.value === paidThroughID);
      
      const newExpenseData: any = {
        ...selectedExpense,
        amount: paymentAmount || selectedExpense.amount || 0,
        description: description,
        reference: reference,
        recurring_expense_id: selectedExpense.id,
        date: paymentDate ? Timestamp.fromDate(paymentDate) : selectedExpense.next_action_date || Timestamp.fromDate(new Date()),
        created_at: serverTimestamp(),
        status: "Recorded",
        paid_through_id: paidThroughID,
        paid_through_name: selectedAccount?.accountName || selectedAccount?.nickname || selectedAccount?.label || "",
        created_by_name: "System Generated"
      };

      delete newExpenseData.id;
      delete newExpenseData.next_action_date;
      delete newExpenseData.frequency;
      delete newExpenseData.start_date;
      delete newExpenseData.end_date;

      await addDoc(collection(db, "companyprofile", companyId, "expenses"), newExpenseData);
      
      const frequency = (selectedExpense.frequency || "monthly").toLowerCase();
      const newDate = selectedExpense.next_action_date ? new Date(selectedExpense.next_action_date.toDate ? selectedExpense.next_action_date.toDate() : selectedExpense.next_action_date.seconds * 1000) : new Date();
      if (frequency === "daily") newDate.setDate(newDate.getDate() + 1);
      else if (frequency === "weekly") newDate.setDate(newDate.getDate() + 7);
      else if (frequency === "monthly") newDate.setMonth(newDate.getMonth() + 1);
      else if (frequency === "yearly") newDate.setFullYear(newDate.getFullYear() + 1);

      await updateDoc(doc(db, "companyprofile", companyId, "recurringexpenses", selectedExpense.id), {
        next_action_date: Timestamp.fromDate(newDate),
        updated_at: serverTimestamp()
      });

      setModalOpened(false);
      setPaidThroughID("");
      fetchAllRecurringExpenses("first");
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAllRecurringExpenses = async (
    direction: "next" | "prev" | "first" = "first",
    newItemsPerPage?: string
  ) => {
    if (!companyId || !branchId) return;
    setIsPageLoading(true);
    try {
      const perPage = parseInt(newItemsPerPage || itemsPerPage || "10");

      let expensesRef = collection(db, "companyprofile", companyId, "recurringexpenses");
      let baseQuery = query(
        expensesRef,
        where("company_id", "==", companyId)
      );

      if (branchId !== "all_branches") {
        baseQuery = query(baseQuery, where("branch_id", "==", branchId));
      }

      // Removed orderBy to avoid requiring a composite index
      // baseQuery = query(baseQuery, orderBy("created_at", "desc"));

      // Count total
      const countSnap = await getCountFromServer(baseQuery);
      setTotalResultCount(countSnap.data().count);

      // Paginate
      let q = query(baseQuery, limit(perPage + 1));

      if (direction === "next" && pageCursors.length > 0) {
        q = query(q, startAfter(pageCursors[pageCursors.length - 1]));
      } else if (direction === "prev" && currentPage > 1) {
        const targetCursorIndex = currentPage - 3;
        if (targetCursorIndex >= 0) {
          q = query(q, startAfter(pageCursors[targetCursorIndex]));
        }
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      const hasNext = docs.length > perPage;
      setHasNextPage(hasNext);

      const visibleDocs = hasNext ? docs.slice(0, perPage) : docs;
      const expensesData = visibleDocs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      expensesData.sort((a: any, b: any) => {
        const dateA = a.created_at?.seconds || 0;
        const dateB = b.created_at?.seconds || 0;
        return dateB - dateA;
      });

      setRecurringExpenses(expensesData);

      if (direction === "next") {
        setPageCursors((prev) => [...prev, visibleDocs[visibleDocs.length - 1]]);
        setCurrentPage((prev) => prev + 1);
      } else if (direction === "prev") {
        setPageCursors((prev) => prev.slice(0, prev.length - 1));
        setCurrentPage((prev) => prev - 1);
      } else {
        const lastDoc = visibleDocs[visibleDocs.length - 1];
        setPageCursors(lastDoc ? [lastDoc] : []);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching recurring expenses:", error);
      logError(error, "fetchAllRecurringExpenses RecurringExpenseTable");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleBranchChange = (value: string | null) => {
    setBranchId(value);
  };

  const breadcrumbslinks = [{
    label: 'Recurring Expenses'
  }]

  return (
    <div className="page">
      <Container size={"lg"}> <Box
            sx={(theme) => ({
              position: "sticky",
              top: 110, // Avoid overlapping the fixed navbar
              zIndex: 100,
              backgroundColor: "#FAFAFA",
              paddingTop: theme.spacing.sm,
              //paddingBottom: theme.spacing.xs,
              marginTop: `-${theme.spacing.sm}`,
            })}
          >
        <SimpleGrid cols={2}>
          <BenchBreadcrumbs links={breadcrumbslinks} />
          <Group position="right">
            <PermissionButton
              resourceId="expenses"
              actionType="create"
              label="Add Recurring Expense"
              to="/recurring-expenses/add"
              target="_self"
            />
          </Group>
        </SimpleGrid>
        <Divider my="sm" /></Box>

        <Group mb="lg" position="apart">
          <div style={{ borderRadius: "6px", marginBottom: "20px" }}></div>
          <Group mb="lg" align="flex-end" position="right">
            {/* Branch Filter */}
            <Select
              label="Filter by Branch"
              value={branchId}
              onChange={setBranchId}
              data={
                branchesList &&
                branchesList?.map((item: any) => ({
                  value: item.id,
                  label: item.name,
                }))
              }
              placeholder="Select Branch"
              maw={200}
            />
          </Group>
        </Group>

        <ScrollArea>
          <BenchTable>
            <thead>
              <tr>
                <th>Expense Name</th>
                <th>Frequency</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody style={{ textTransform: "capitalize" }}>
              {(() => {
                if (isPageLoading) {
                  return (
                    <tr style={{ height: "200px" }}>
                      <td colSpan={7} style={{ textAlign: "center", verticalAlign: "middle" }}>
                        <Loader />
                      </td>
                    </tr>
                  );
                }
                if (recurringExpenses.length === 0) {
                  return (
                    <tr style={{ height: "200px" }}>
                      <td colSpan={7} style={{ textAlign: "center", verticalAlign: "middle" }}>
                        No Data Available
                      </td>
                    </tr>
                  );
                }

                return recurringExpenses.map((expense) => (
                  <tr 
                    key={expense.id}
                    onClick={() => navigate(`/recurringexpenseviewpage/${expense.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{expense.expense_name || "No Data"}</td>
                    <td>{expense.frequency || "Monthly"}</td>
                    <td>
                      {expense?.start_date
                        ? typeof expense.start_date === "object" && expense.start_date.seconds
                          ? new Date(expense.start_date.seconds * 1000).toLocaleDateString("en-IN")
                          : typeof expense.start_date === "number"
                            ? new Date(expense.start_date * 1000).toLocaleDateString("en-IN")
                            : expense.start_date instanceof Date
                              ? expense.start_date.toLocaleDateString("en-IN")
                              : "Invalid Date"
                        : "No Date"}
                    </td>
                    <td>
                      {expense?.end_date
                        ? typeof expense.end_date === "object" && expense.end_date.seconds
                          ? new Date(expense.end_date.seconds * 1000).toLocaleDateString("en-IN")
                          : typeof expense.end_date === "number"
                            ? new Date(expense.end_date * 1000).toLocaleDateString("en-IN")
                            : expense.end_date instanceof Date
                              ? expense.end_date.toLocaleDateString("en-IN")
                              : "Invalid Date"
                        : "Never"}
                    </td>
                    <td>
                      <Text color={expense.status === "Active" ? "green" : "red"} weight={500}>
                        {expense.status || "Unknown"}
                      </Text>
                    </td>
                    <td>₹ {expense.amount || "0.00"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <Group spacing="sm">
                          <Menu
                            withinPortal
                            withArrow
                            shadow="md"
                            position="bottom-end"
                          >
                            <Menu.Target>
                              <ActionIcon variant="default">
                                <FcExpand />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => navigate(`/recurring-expenses/edit/${expense.id}`)}>
                                Edit Expense
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                onClick={() => {
                                  setSelectedExpense(expense);
                                  setPaymentAmount(expense.amount ? Number(expense.amount) : "");
                                  setPaymentDate(
                                    expense.next_action_date
                                      ? (expense.next_action_date.toDate ? expense.next_action_date.toDate() : new Date(expense.next_action_date.seconds * 1000))
                                      : new Date()
                                  );
                                  setReference("");
                                  setDescription(expense.description || "");
                                  setModalOpened(true);
                                }}
                              >
                                Record Payment
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </div>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </BenchTable>
        </ScrollArea>

        {/* Pagination Section */}
        <div style={{ marginTop: "1rem", display: "flex", marginBottom: "2rem", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Text>Rows per page : </Text>
            <Select
              style={{ width: "100px", marginLeft: "5px" }}
              value={itemsPerPage}
              onChange={(val) => {
                setItemsPerPage(val || "10");
                fetchAllRecurringExpenses("first", val || "10");
              }}
              data={[
                { value: "10", label: "10" },
                { value: "25", label: "25" },
                { value: "50", label: "50" },
              ]}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Group>
              <Button leftIcon={<IconArrowLeft size={14} />} disabled={currentPage === 1} onClick={() => fetchAllRecurringExpenses("prev")}>
                Prev
              </Button>
              <Text>{currentPage}</Text>
              <Button rightIcon={<IconArrowRight size={14} />} onClick={() => fetchAllRecurringExpenses("next")} disabled={!hasNextPage}>
                Next
              </Button>
            </Group>
          </div>
        </div>
        <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Record Manual Expense" centered size="lg">
          <SimpleGrid cols={2} mb="md">
            <DateInput
              label="Payment Date"
              placeholder="Select date"
              value={paymentDate}
              onChange={setPaymentDate}
              withAsterisk
            />
            <TextInput
              type="number"
              label="Amount"
              placeholder="Enter amount"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value as any)}
              withAsterisk
            />
          </SimpleGrid>

          <Select
            label="Paid Through"
            placeholder="Select an account"
            data={paymentAccountList.map((a) => ({ value: a.id || a.value, label: a.accountName || a.nickname || a.label }))}
            value={paidThroughID}
            onChange={(val) => setPaidThroughID(val || "")}
            withAsterisk
            mb="md"
          />

          <TextInput
            label="Reference Number"
            placeholder="Enter reference or transaction ID"
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            mb="md"
          />

          <Textarea
            label="Description / Notes"
            placeholder="Add any additional notes"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={3}
            mb="xl"
          />

          <Group position="right">
            <Button variant="default" onClick={() => setModalOpened(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} loading={isSubmitting} disabled={!paidThroughID || !paymentAmount || !paymentDate}>Record</Button>
          </Group>
        </Modal>
      </Container>
    </div>
  );
}
